import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { reports as reportsApi, hotels } from "../api/endpoints";
import SearchableSelect from "../components/SearchableSelect";
import { DataTableShell, EmptyState, FilterToolbar, MetricStrip, PageHeader, SectionCard, SegmentedViewSwitch } from "../components/Workspace";
import { triggerDownload } from "../utils/download";
import type {
  ShopperCallSummary, ServiceLevelReport, CallerFeeSummary, CallerFeeByType,
  HMCMonthlySummaryRow, ScheduledVsCompletedRow, CallStatusByMonthRow,
  DuplicateScheduledRow, ManagementCompany, ExceptionalCallOut,
  BusinessIntelligenceOut,
} from "../types";

type Tab =
  | "shopper"
  | "service-level"
  | "fees"
  | "fees-by-type"
  | "hmc-summary"
  | "sched-vs-complete"
  | "status-by-month"
  | "duplicates"
  | "exceptional"
  | "bi";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MIN_REPORT_YEAR = 2013;

const TAB_ITEMS: {
  key: Tab;
  label: string;
  description: string;
  exportKey?: string;
}[] = [
  { key: "shopper", label: "By Shopper", description: "Scheduled, completed, and pending volume by shopper.", exportKey: "calls-by-shopper" },
  { key: "service-level", label: "Service Level", description: "Completed and pending volume by service level.", exportKey: "calls-by-service-level" },
  { key: "fees", label: "Caller Fees", description: "Fee totals by shopper for the active period.", exportKey: "caller-fees" },
  { key: "fees-by-type", label: "Fees by Type", description: "Fee mix split across standard, proposal, RFP, and international work.", exportKey: "caller-fees-by-type" },
  { key: "hmc-summary", label: "HMC Summary", description: "Detailed monthly summary rows for the selected management company scope.", exportKey: "hmc-monthly-summary" },
  { key: "sched-vs-complete", label: "Sched vs Complete", description: "Year matrix of scheduled versus completed volume by shopper.", exportKey: "scheduled-vs-completed" },
  { key: "status-by-month", label: "Status by Month", description: "Call status mix across months in the current year." },
  { key: "duplicates", label: "Duplicates", description: "Properties with multiple scheduled calls in the same month." },
  { key: "exceptional", label: "Exceptional", description: "Overdue or exceptional calls that need attention." },
  { key: "bi", label: "BI", description: "Operational insight summary for volume, billing, and risk." },
];

function formatCurrency(value: number) {
  return `$${value.toFixed(2)}`;
}

function ReportTable({
  title,
  subtitle,
  exportKey,
  onExport,
  children,
}: {
  title: string;
  subtitle: string;
  exportKey?: string;
  onExport: (report: string) => void;
  children: React.ReactNode;
}) {
  return (
    <DataTableShell
      title={title}
      subtitle={subtitle}
      actions={
        exportKey ? (
          <button type="button" onClick={() => onExport(exportKey)} className="button-secondary">
            Export Excel
          </button>
        ) : undefined
      }
    >
      {children}
    </DataTableShell>
  );
}

export default function ReportsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [mcId, setMcId] = useState<number | undefined>(undefined);
  const [mcList, setMcList] = useState<ManagementCompany[]>([]);

  const [shopperData, setShopperData] = useState<ShopperCallSummary[]>([]);
  const [serviceLevelData, setServiceLevelData] = useState<ServiceLevelReport[]>([]);
  const [feeData, setFeeData] = useState<CallerFeeSummary[]>([]);
  const [feeByTypeData, setFeeByTypeData] = useState<CallerFeeByType[]>([]);
  const [hmcSummary, setHmcSummary] = useState<HMCMonthlySummaryRow[]>([]);
  const [schedVsComplete, setSchedVsComplete] = useState<ScheduledVsCompletedRow[]>([]);
  const [statusByMonth, setStatusByMonth] = useState<CallStatusByMonthRow[]>([]);
  const [duplicates, setDuplicates] = useState<DuplicateScheduledRow[]>([]);
  const [exceptionalData, setExceptionalData] = useState<ExceptionalCallOut[]>([]);
  const [biData, setBiData] = useState<BusinessIntelligenceOut | null>(null);

  const reportKeys = TAB_ITEMS.map((item) => item.key);
  const hashTab = location.hash.replace("#", "");
  const tab = reportKeys.includes(hashTab as Tab) ? (hashTab as Tab) : "shopper";
  const activeTab = TAB_ITEMS.find((item) => item.key === tab) || TAB_ITEMS[0];

  const params = useMemo(
    () => `year=${year}&month=${month}${mcId ? `&mc_id=${mcId}` : ""}`,
    [year, month, mcId]
  );
  const periodLabel = `${MONTH_NAMES[month - 1]} ${year}`;
  const availableYears = useMemo(
    () => Array.from({ length: currentYear - MIN_REPORT_YEAR + 2 }, (_, index) => MIN_REPORT_YEAR + index).reverse(),
    [currentYear]
  );
  const selectedMcName = mcId ? mcList.find((mc) => mc.id === mcId)?.name || "Selected HMC" : "All HMCs";

  useEffect(() => {
    hotels.managementCompanies.list("limit=500").then((res) => setMcList(res.items));
  }, []);

  useEffect(() => {
    if (tab === "shopper") reportsApi.callsByShopper(params).then(setShopperData);
    if (tab === "service-level") reportsApi.callsByServiceLevel(params).then(setServiceLevelData);
    if (tab === "fees") reportsApi.callerFees(params).then(setFeeData);
    if (tab === "fees-by-type") reportsApi.callerFeesByType(params).then(setFeeByTypeData);
    if (tab === "hmc-summary") reportsApi.hmcMonthlySummary(params).then(setHmcSummary);
    if (tab === "sched-vs-complete") reportsApi.scheduledVsCompleted(params).then(setSchedVsComplete);
    if (tab === "status-by-month") reportsApi.callStatusByMonth(year, month, mcId).then(setStatusByMonth);
    if (tab === "duplicates") reportsApi.duplicateScheduledCalls(year, month, mcId).then(setDuplicates);
    if (tab === "exceptional") reportsApi.exceptionalCalls(params).then(setExceptionalData);
    if (tab === "bi") reportsApi.businessIntelligence(params).then(setBiData);
  }, [tab, year, month, mcId, params]);

  useEffect(() => {
    if (!location.hash || !reportKeys.includes(hashTab as Tab)) {
      navigate({ pathname: "/reports", hash: "#shopper" }, { replace: true });
    }
  }, [hashTab, location.hash, navigate, reportKeys]);

  const downloadExcel = async (report: string) => {
    try {
      await triggerDownload(reportsApi.exportUrl(report, params));
    } catch (error) {
      window.alert(error instanceof Error ? error.message : "Download failed");
    }
  };

  const rowCount =
    tab === "shopper" ? shopperData.length :
    tab === "service-level" ? serviceLevelData.length :
    tab === "fees" ? feeData.length :
    tab === "fees-by-type" ? feeByTypeData.length :
    tab === "hmc-summary" ? hmcSummary.length :
    tab === "sched-vs-complete" ? schedVsComplete.length :
    tab === "status-by-month" ? statusByMonth.length :
    tab === "duplicates" ? duplicates.length :
    tab === "exceptional" ? exceptionalData.length :
    biData ? biData.insights.length : 0;

  const kpiItems =
    tab === "bi" && biData
      ? [
          { label: "Scheduled calls", value: biData.summary.scheduled_calls, detail: `Scope ${periodLabel}`, tone: "brand" as const },
          { label: "Completion rate", value: `${biData.summary.completion_rate.toFixed(1)}%`, detail: `${biData.summary.completed_calls} completed`, tone: "success" as const },
          { label: "Billing", value: `$${biData.summary.billed_amount.toFixed(0)}`, detail: `Fees $${biData.summary.shopper_fees.toFixed(0)}`, tone: "accent" as const },
          { label: "Operational risk", value: biData.summary.overdue_calls + biData.summary.duplicate_properties, detail: "Overdue plus duplicate-property signals", tone: "danger" as const },
        ]
      : [
          { label: "Active report", value: activeTab.label, detail: activeTab.description, tone: "brand" as const },
          { label: "Period", value: periodLabel, detail: tab === "sched-vs-complete" ? `Calendar year ${year}` : "Current reporting window", tone: "neutral" as const },
          { label: "Scope", value: selectedMcName, detail: mcId ? "Management company filtered" : "All management companies", tone: "accent" as const },
          { label: "Rows", value: rowCount, detail: "Loaded rows in the active report", tone: "success" as const },
        ];

  const renderContent = () => {
    switch (tab) {
      case "shopper":
        return (
          <ReportTable
            title="Calls by shopper"
            subtitle={`Scheduled, completed, and pending counts for ${periodLabel}.`}
            exportKey={activeTab.exportKey}
            onExport={downloadExcel}
          >
            <div className="overflow-x-auto">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th>Shopper</th>
                    <th className="text-right">Scheduled</th>
                    <th className="text-right">Completed</th>
                    <th className="text-right">Pending</th>
                  </tr>
                </thead>
                <tbody>
                  {shopperData.map((row) => (
                    <tr key={row.shopper_id}>
                      <td>{row.shopper_name}</td>
                      <td className="text-right font-semibold">{row.scheduled}</td>
                      <td className="text-right font-semibold">{row.completed}</td>
                      <td className="text-right font-semibold">{row.pending}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {shopperData.length === 0 ? <EmptyState title="No shopper rows" message={`No shopper rows for ${periodLabel}.`} /> : null}
          </ReportTable>
        );
      case "service-level":
        return (
          <ReportTable
            title="Calls by service level"
            subtitle={`Completed and pending mix by service level for ${periodLabel}.`}
            exportKey={activeTab.exportKey}
            onExport={downloadExcel}
          >
            <div className="overflow-x-auto">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th>Service level</th>
                    <th className="text-right">Total</th>
                    <th className="text-right">Completed</th>
                    <th className="text-right">Pending</th>
                  </tr>
                </thead>
                <tbody>
                  {serviceLevelData.map((row, index) => (
                    <tr key={`${row.service_level}-${index}`}>
                      <td>{row.service_level}</td>
                      <td className="text-right font-semibold">{row.total}</td>
                      <td className="text-right font-semibold">{row.completed}</td>
                      <td className="text-right font-semibold">{row.pending}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {serviceLevelData.length === 0 ? <EmptyState title="No service levels" message={`No service level rows for ${periodLabel}.`} /> : null}
          </ReportTable>
        );
      case "fees":
        return (
          <ReportTable
            title="Caller fees"
            subtitle={`Fee totals by shopper for ${periodLabel}.`}
            exportKey={activeTab.exportKey}
            onExport={downloadExcel}
          >
            <div className="overflow-x-auto">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th>Shopper</th>
                    <th className="text-right">Total calls</th>
                    <th className="text-right">Total fees</th>
                  </tr>
                </thead>
                <tbody>
                  {feeData.map((row) => (
                    <tr key={row.shopper_id}>
                      <td>{row.shopper_name}</td>
                      <td className="text-right font-semibold">{row.total_calls}</td>
                      <td className="text-right font-semibold">{formatCurrency(row.total_fees)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {feeData.length === 0 ? <EmptyState title="No fee rows" message={`No fee rows for ${periodLabel}.`} /> : null}
          </ReportTable>
        );
      case "fees-by-type":
        return (
          <ReportTable
            title="Caller fees by type"
            subtitle={`Fee mix by shopper for ${periodLabel}.`}
            exportKey={activeTab.exportKey}
            onExport={downloadExcel}
          >
            <div className="overflow-x-auto">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th>Shopper</th>
                    <th className="text-right">Standard Qty</th>
                    <th className="text-right">Standard $</th>
                    <th className="text-right">Proposal Qty</th>
                    <th className="text-right">Proposal $</th>
                    <th className="text-right">RFP Qty</th>
                    <th className="text-right">RFP $</th>
                    <th className="text-right">Intl Qty</th>
                    <th className="text-right">Intl $</th>
                  </tr>
                </thead>
                <tbody>
                  {feeByTypeData.map((row) => (
                    <tr key={row.shopper_id}>
                      <td>{row.shopper_name}</td>
                      <td className="text-right">{row.standard_qty}</td>
                      <td className="text-right">{formatCurrency(row.standard_fees)}</td>
                      <td className="text-right">{row.proposal_qty}</td>
                      <td className="text-right">{formatCurrency(row.proposal_fees)}</td>
                      <td className="text-right">{row.rfp_qty}</td>
                      <td className="text-right">{formatCurrency(row.rfp_fees)}</td>
                      <td className="text-right">{row.international_qty}</td>
                      <td className="text-right">{formatCurrency(row.international_fees)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {feeByTypeData.length === 0 ? <EmptyState title="No fee mix rows" message={`No fee-by-type rows for ${periodLabel}.`} /> : null}
          </ReportTable>
        );
      case "hmc-summary":
        return (
          <ReportTable
            title="HMC monthly summary"
            subtitle={`Detailed summary rows for ${periodLabel}.`}
            exportKey={activeTab.exportKey}
            onExport={downloadExcel}
          >
            <div className="overflow-x-auto">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th>Shop ID</th>
                    <th>HMC</th>
                    <th>Hotel</th>
                    <th>Type</th>
                    <th>SL</th>
                    <th>Shopper</th>
                    <th>Status</th>
                    <th className="text-right">%</th>
                    <th className="text-right">Admin</th>
                    <th className="text-right">Hosp</th>
                    <th className="text-right">Qual</th>
                    <th className="text-right">Sell</th>
                    <th className="text-right">Close</th>
                    <th className="text-right">RFP</th>
                    <th>Completed</th>
                    <th>Reshop</th>
                  </tr>
                </thead>
                <tbody>
                  {hmcSummary.map((row) => (
                    <tr key={row.call_id}>
                      <td className="font-mono text-xs">{row.shop_id || row.call_id}</td>
                      <td>{row.management_company_name}</td>
                      <td>
                        <div className="font-medium">{row.hotel_name}</div>
                        <div className="text-xs text-slate-500">{row.hotel_code}</div>
                      </td>
                      <td>{row.call_type}</td>
                      <td>{row.service_level}</td>
                      <td>{row.shopper_name || row.shopper_initials}</td>
                      <td>{row.status}</td>
                      <td className="text-right font-semibold">{row.percentage != null ? `${row.percentage}%` : "-"}</td>
                      <td className="text-right">{row.administration ?? "-"}</td>
                      <td className="text-right">{row.hospitality ?? "-"}</td>
                      <td className="text-right">{row.qualifying ?? "-"}</td>
                      <td className="text-right">{row.selling ?? "-"}</td>
                      <td className="text-right">{row.closing ?? "-"}</td>
                      <td className="text-right">{row.prop_rfp ?? "-"}</td>
                      <td>{row.date_completed || "-"}</td>
                      <td>{row.is_reshop ? "Yes" : "No"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hmcSummary.length === 0 ? <EmptyState title="No HMC rows" message={`No HMC summary rows for ${periodLabel}.`} /> : null}
          </ReportTable>
        );
      case "sched-vs-complete":
        return (
          <ReportTable
            title="Scheduled vs completed"
            subtitle={`Calendar year matrix for ${year}.`}
            exportKey={activeTab.exportKey}
            onExport={downloadExcel}
          >
            <div className="overflow-x-auto">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th>Shopper</th>
                    {MONTH_NAMES.map((monthName) => (
                      <th key={monthName} className="text-center">{monthName}</th>
                    ))}
                    <th className="text-right">Total Sched</th>
                    <th className="text-right">Total Comp</th>
                  </tr>
                </thead>
                <tbody>
                  {schedVsComplete.map((row) => (
                    <tr key={row.shopper_id}>
                      <td>{row.shopper_name}</td>
                      {Array.from({ length: 12 }, (_, index) => {
                        const monthRow = row.months.find((monthItem) => monthItem.month === index + 1);
                        return <td key={index} className="text-center text-xs">{monthRow ? `${monthRow.scheduled}/${monthRow.completed}` : "-"}</td>;
                      })}
                      <td className="text-right font-semibold">{row.total_scheduled}</td>
                      <td className="text-right font-semibold">{row.total_completed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {schedVsComplete.length === 0 ? <EmptyState title="No schedule rows" message={`No scheduled versus completed rows for ${year}.`} /> : null}
          </ReportTable>
        );
      case "status-by-month":
        return (
          <ReportTable
            title="Call status by month"
            subtitle={`Status mix for ${year}, filtered to ${selectedMcName}.`}
            onExport={downloadExcel}
          >
            <div className="overflow-x-auto">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Status</th>
                    <th className="text-right">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {statusByMonth.map((row, index) => (
                    <tr key={`${row.month}-${row.status}-${index}`}>
                      <td>{MONTH_NAMES[row.month - 1]}</td>
                      <td>{row.status}</td>
                      <td className="text-right font-semibold">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {statusByMonth.length === 0 ? <EmptyState title="No status rows" message={`No status rows for ${year}.`} /> : null}
          </ReportTable>
        );
      case "duplicates":
        return (
          <ReportTable
            title="Duplicate scheduled calls"
            subtitle={`Properties with multiple calls scheduled in ${periodLabel}.`}
            onExport={downloadExcel}
          >
            <div className="overflow-x-auto">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th>Hotel Code</th>
                    <th>Property</th>
                    <th>Month</th>
                    <th className="text-right">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {duplicates.map((row, index) => (
                    <tr key={`${row.hotel_code}-${row.month}-${index}`}>
                      <td className="font-mono text-xs">{row.hotel_code}</td>
                      <td>{row.hotel_name}</td>
                      <td>{MONTH_NAMES[row.month - 1]}</td>
                      <td className="text-right font-semibold text-amber-700">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {duplicates.length === 0 ? <EmptyState title="No duplicates" message={`No duplicate scheduled calls for ${periodLabel}.`} /> : null}
          </ReportTable>
        );
      case "exceptional":
        return (
          <ReportTable
            title="Exceptional and overdue calls"
            subtitle={`Calls needing attention in ${periodLabel}.`}
            onExport={downloadExcel}
          >
            <div className="overflow-x-auto">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th>Call ID</th>
                    <th>Property</th>
                    <th>Shopper</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {exceptionalData.map((row) => (
                    <tr key={row.call_id}>
                      <td>#{row.call_id}</td>
                      <td>{row.property_name}</td>
                      <td>{row.shopper_name}</td>
                      <td>{row.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {exceptionalData.length === 0 ? <EmptyState title="No exceptional calls" message={`No exceptional or overdue calls for ${periodLabel}.`} /> : null}
          </ReportTable>
        );
      case "bi":
        return biData ? (
          <div className="space-y-5">
            <SectionCard title="Insights" subtitle={`Operational signals for ${periodLabel}.`}>
              <div className="grid gap-4 xl:grid-cols-[1.2fr,0.8fr]">
                <div className="space-y-4">
                  {biData.insights.map((insight) => (
                    <article key={insight.title} className="surface-muted p-4">
                      <div className="text-sm font-semibold text-slate-900">{insight.title}</div>
                      <div className="mt-1 text-sm text-slate-600">{insight.detail}</div>
                    </article>
                  ))}
                </div>
                <div className="surface-muted p-5">
                  <div className="label-caps">Status mix</div>
                  <div className="mt-4 space-y-3">
                    {biData.status_mix.map((row) => (
                      <div key={row.status}>
                        <div className="flex items-center justify-between text-sm">
                          <span>{row.status}</span>
                          <span className="font-semibold">{row.count}</span>
                        </div>
                        <div className="progress-track mt-2">
                          <div
                            className="progress-fill"
                            style={{ width: `${biData.summary.scheduled_calls ? (row.count / biData.summary.scheduled_calls) * 100 : 0}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </SectionCard>

            <section className="grid gap-5 xl:grid-cols-3">
              <SectionCard title="Top HMCs" subtitle="Highest active management company volume in the selected period.">
                <div className="space-y-3">
                  {biData.top_hmcs.length > 0 ? biData.top_hmcs.map((row) => (
                    <article key={row.id} className="surface-muted p-4">
                      <div className="font-medium text-slate-900">{row.name}</div>
                      <div className="mt-1 text-sm text-slate-500">
                        {row.total_calls} calls | {row.completed_calls} completed | {row.completion_rate.toFixed(1)}%
                      </div>
                    </article>
                  )) : <EmptyState title="No HMC volume" message={`No HMC volume for ${periodLabel}.`} />}
                </div>
              </SectionCard>
              <SectionCard title="Top Shoppers" subtitle="Highest shopper volume in the selected period.">
                <div className="space-y-3">
                  {biData.top_shoppers.length > 0 ? biData.top_shoppers.map((row) => (
                    <article key={row.id} className="surface-muted p-4">
                      <div className="font-medium text-slate-900">{row.name}</div>
                      <div className="mt-1 text-sm text-slate-500">
                        {row.total_calls} calls | {row.completed_calls} completed | {row.completion_rate.toFixed(1)}%
                      </div>
                    </article>
                  )) : <EmptyState title="No shopper assignments" message={`No shopper assignments for ${periodLabel}.`} />}
                </div>
              </SectionCard>
              <SectionCard title="Service Levels" subtitle="Performance mix across active service levels.">
                <div className="space-y-3">
                  {biData.service_levels.length > 0 ? biData.service_levels.map((row) => (
                    <article key={row.name} className="surface-muted p-4">
                      <div className="font-medium text-slate-900">{row.name}</div>
                      <div className="mt-1 text-sm text-slate-500">
                        {row.total_calls} calls | {row.completed_calls} completed | {row.completion_rate.toFixed(1)}%
                      </div>
                      <div className="mt-1 text-xs text-slate-400">
                        Avg score {row.average_score != null ? `${row.average_score.toFixed(1)}%` : "N/A"}
                      </div>
                    </article>
                  )) : <EmptyState title="No service levels" message={`No service level data for ${periodLabel}.`} />}
                </div>
              </SectionCard>
            </section>
          </div>
        ) : (
          <SectionCard title="Business intelligence" subtitle="Loading BI summary...">
            <div className="text-sm text-slate-500">Loading business intelligence data.</div>
          </SectionCard>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Reporting workspace"
        title={activeTab.label}
        description={activeTab.description}
        serifTitle
        badges={[
          { label: periodLabel, tone: "neutral" },
          { label: selectedMcName, tone: "brand" },
        ]}
      />

      <SectionCard title="Report rail" subtitle="Switch reports inside the page without changing the surrounding workspace.">
        <SegmentedViewSwitch
          items={TAB_ITEMS.map((item) => ({ key: item.key, label: item.label }))}
          activeKey={tab}
          onChange={(key) => navigate({ pathname: "/reports", hash: `#${key}` })}
        />
      </SectionCard>

      <FilterToolbar sticky>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[7rem_7rem_minmax(15rem,1fr)_auto] xl:items-end">
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Year</span>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="select-shell w-full">
              {availableYears.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Month</span>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="select-shell w-full"
              disabled={tab === "sched-vs-complete"}
            >
              {MONTH_NAMES.map((label, index) => <option key={label} value={index + 1}>{label}</option>)}
            </select>
          </label>
          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Management Company</span>
            <SearchableSelect
              options={mcList.map((mc) => ({ value: mc.id, label: mc.name }))}
              value={mcId ?? ""}
              onChange={(value) => setMcId(value ? Number(value) : undefined)}
              placeholder="All HMCs"
              className="w-full"
            />
          </label>
          <button
            type="button"
            onClick={() => activeTab.exportKey && downloadExcel(activeTab.exportKey)}
            className="button-secondary h-11 !px-4 !py-2 text-sm"
            disabled={!activeTab.exportKey}
          >
            {activeTab.exportKey ? "Export active report" : "No export"}
          </button>
        </div>
      </FilterToolbar>

      <MetricStrip items={kpiItems} />

      {renderContent()}

      <SectionCard title="Additional exports" subtitle="Legacy exports that remain useful outside the active report tab.">
        <div className="flex flex-wrap gap-3">
          <button onClick={() => downloadExcel("annual-report")} className="button-ghost">Annual Report</button>
          <button onClick={() => downloadExcel("reshop-completion")} className="button-ghost">Reshop Completion</button>
          <button onClick={() => downloadExcel("hmc-billing")} className="button-ghost">HMC Billing</button>
          <button className="button-ghost opacity-50" disabled>Shopper Calls</button>
        </div>
      </SectionCard>
    </div>
  );
}
